import { __, sprintf } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { useQuoteActions } from '../../hooks/useQuoteActions';
import { Quote } from '../../types/quote';
import { QUOTE_STATUS } from '../../constants';
import { DokanButton, DokanModal } from '@dokan/components';
import { decodeHTML } from '../../helper';

interface QuoteActionButtonsProps {
    quote: Quote;
    quoteUpdated: boolean;
}

interface ConfirmationAction {
    action: string;
    handler: () => void;
    messages: {
        title: string;
        message: string;
        confirmText: string;
    };
}

interface ConfirmationMessages {
    [ key: string ]: {
        title: string;
        message: string;
        confirmText: string;
    };
}

const QuoteActionButtons: React.FC< QuoteActionButtonsProps > = ( {
    quote,
    quoteUpdated,
} ) => {
    const {
        handleReject,
        handleReopen,
        handleApprove,
        handleConvert,
        isLoading,
    } = useQuoteActions( quote?.quote_id );

    const [ showConfirmation, setShowConfirmation ] =
        useState< boolean >( false );
    const [ confirmationAction, setConfirmationAction ] =
        useState< ConfirmationAction | null >( null );

    const handleAction = (
        action: string,
        handler: () => void,
        args: any = null
    ) => {
        setConfirmationAction( {
            action,
            handler: () => handler( args ),
            messages: getConfirmationMessages( action ),
        } );
        setShowConfirmation( true );
    };

    const getConfirmationMessages = (
        action: string
    ): ConfirmationMessages[ keyof ConfirmationMessages ] => {
        const messages: ConfirmationMessages = {
            reject: {
                title: __( 'Reject Quote', 'dokan' ),
                message: __(
                    'Are you sure you want to reject this quote? This action cannot be undone.',
                    'dokan'
                ),
                confirmText: __( 'Yes, Reject', 'dokan' ),
            },
            reopen: {
                title: __( 'Reopen Quote', 'dokan' ),
                message: __(
                    'Are you sure you want to reopen this quote? This will allow the customer to make changes to their request.',
                    'dokan'
                ),
                confirmText: __( 'Yes, Reopen', 'dokan' ),
            },
            approve: {
                title: __( 'Approve Quote', 'dokan' ),
                message: __(
                    'Are you sure you want to approve this quote? This will send the quote to the customer for acceptance.',
                    'dokan'
                ),
                confirmText: __( 'Yes, Approve', 'dokan' ),
            },
            update: {
                title: __( 'Update Quote', 'dokan' ),
                message: __(
                    'Are you sure you want to update this quote? This will send the updated quote to the customer for review.',
                    'dokan'
                ),
                confirmText: __( 'Yes, Update', 'dokan' ),
            },
            convert: {
                title: __( 'Convert to Order', 'dokan' ),
                message: __(
                    'Are you sure you want to convert this quote to an order? This action cannot be undone.',
                    'dokan'
                ),
                confirmText: __( 'Yes, Convert', 'dokan' ),
            },
            accept: {
                title: __( 'Accept Quote', 'dokan' ),
                message: __(
                    'Are you sure you want to accept this quote? This will create an order with the quoted items.',
                    'dokan'
                ),
                confirmText: __( 'Yes, Accept', 'dokan' ),
            },
        };

        return messages[ action ];
    };

    const renderButtons = () => {
        const status = quote?.status;
        const order_id = quote?.order_id;
        const order_url = quote?.order_url;
        const buttons = [];
        const product_ids: number[] = [];
        const offer_price: Record< number, number > = {};
        const offer_product_quantity: Record< number, number > = {};

        quote?.products?.forEach( ( product ) => {
            if ( product?.id ) {
                product_ids.push( product.id );
                if ( product?.offer_price ) {
                    offer_price[ product.id ] = product.offer_price;
                }
                if ( product?.offer_product_quantity ) {
                    offer_product_quantity[ product.id ] =
                        product.offer_product_quantity;
                }
            }
        } );

        const args = {
            product_ids,
            offer_price,
            offer_product_quantity,
            shipping_cost: quote?.shipping_cost || 0,
            vendor_msg: quote?.store_info?.vendor_additional_msg || '',
        };

        // Vendor Reject Button
        if (
            [
                QUOTE_STATUS.PENDING,
                QUOTE_STATUS.UPDATED,
                QUOTE_STATUS.APPROVE,
                QUOTE_STATUS.ACCEPTED,
            ].includes( status as keyof typeof QUOTE_STATUS )
        ) {
            buttons.push(
                <DokanButton
                    key="reject"
                    variant="danger"
                    disabled={ isLoading }
                    onClick={ () => handleAction( 'reject', handleReject ) }
                >
                    { __( 'Reject the Deal', 'dokan' ) }
                </DokanButton>
            );
        }

        // Reopen Button
        if (
            [ QUOTE_STATUS.REJECT, QUOTE_STATUS.EXPIRED ].includes(
                status as keyof typeof QUOTE_STATUS
            )
        ) {
            buttons.push(
                <DokanButton
                    key="reopen"
                    variant="secondary"
                    disabled={ isLoading }
                    onClick={ () => handleAction( 'reopen', handleReopen ) }
                >
                    { __( 'Re-Open', 'dokan' ) }
                </DokanButton>
            );
        }

        // Approve Button
        if ( status === QUOTE_STATUS.PENDING ) {
            buttons.push(
                <DokanButton
                    variant="secondary"
                    disabled={ isLoading }
                    key={ quoteUpdated ? 'update' : 'approve' }
                    onClick={ () =>
                        handleAction(
                            quoteUpdated ? 'update' : 'approve',
                            handleApprove,
                            args
                        )
                    }
                >
                    { sprintf(
                        /* translators: %s: Approve or Update */
                        __( '%s', 'dokan' ),
                        quoteUpdated ? 'Update' : 'Approve'
                    ) }
                </DokanButton>
            );
        }

        // Convert to Order Button - Vendor
        if (
            [ QUOTE_STATUS.APPROVE, QUOTE_STATUS.ACCEPTED ].includes(
                status as keyof typeof QUOTE_STATUS
            ) &&
            ! Boolean( parseInt( order_id?.toString() || '0' ) )
        ) {
            buttons.push(
                <DokanButton
                    key="convert-vendor"
                    variant="secondary"
                    disabled={ isLoading }
                    onClick={ () => handleAction( 'convert', handleConvert ) }
                >
                    { __( 'Convert to Order', 'dokan' ) }
                </DokanButton>
            );
        }

        // View Order Button
        if ( order_id && status === QUOTE_STATUS.CONVERTED ) {
            buttons.push(
                <DokanButton
                    link={ true }
                    key="view-order"
                    variant="primary"
                    target="_blank"
                    disabled={ isLoading }
                    href={ decodeHTML( order_url ) }
                >
                    { __( 'View Order', 'dokan' ) }
                </DokanButton>
            );
        }

        return buttons;
    };

    return (
        <>
            <div className="flex gap-2 justify-end">{ renderButtons() }</div>

            <DokanModal
                loading={ isLoading }
                className="min-w-96"
                isOpen={ showConfirmation }
                onConfirm={ () => {
                    confirmationAction?.handler();
                    setShowConfirmation( false );
                } }
                confirmButtonText={ confirmationAction?.messages?.confirmText }
                namespace="quote-action-confirmation"
                onClose={ () => setShowConfirmation( false ) }
                dialogHeader={ confirmationAction?.messages?.title }
                confirmationTitle={ confirmationAction?.messages?.title }
                confirmationDescription={
                    confirmationAction?.messages?.message
                }
            />
        </>
    );
};

export default QuoteActionButtons;
